import React, {
  type ChangeEvent,
  type CSSProperties,
  type FormEvent,
  forwardRef,
  type RefObject,
} from "react";
import { IconSearch } from "@purpur/icon/search";
import { TextField, type TextFieldProps } from "@purpur/text-field";
import { type AutocompleteOption } from "@purpur/use-autocomplete";
import c from "classnames/bind";

import styles from "./search-field.module.scss";
import { SearchFieldButton } from "./search-field-button";

export const searchFieldVariants = ["button-attached", "no-button"] as const;

type Variant = (typeof searchFieldVariants)[number];

type DefaultProps = {
  ["data-testid"]?: string;
  /**
   * An accessible label for the clear button.
   * */
  clearButtonAriaLabel: string;
  /**
   * Disables both the input field and submit button if `true`.
   * */
  disabled?: boolean;
  /**
   * Will display only a search icon in the search button if `true`.
   * */
  iconOnlySearchButton?: boolean;
  /**
   * Event handler called when the value of the search field changes.
   * */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Event handler called when the clear button is clicked.
   * */
  onClear: () => void;
  /**
   * Event handler called when the search is triggered.
   * */
  onSearch?: (event?: React.SyntheticEvent) => void;
  /**
   * Controls how the search field handles submission.
   * - `"form"` (default): Renders a `<form>` element and submits via form submission.
   * - `"manual"`: Renders a `<div>` instead of a `<form>` element.
   *   Use this when the component is nested inside another form.
   * */
  submitBehavior?: "form" | "manual";
  /**
   * The label text (or `aria-label` if `iconOnlySearchButton` is `true`) of the search button.
   * */
  searchButtonLabel?: string;
  variant: Variant;
  formRef?: RefObject<HTMLFormElement>;
};

type TextSearchButton = {
  iconOnlySearchButton?: false;
  searchButtonLabel: string;
  variant: "button-attached";
};

type IconOnlySearchButton = {
  iconOnlySearchButton: true;
  searchButtonLabel: string;
  variant: "button-attached";
};

type NoButton = {
  searchButtonLabel?: never;
  variant: "no-button";
};

export type SearchFieldBaseProps = DefaultProps &
  (TextSearchButton | IconOnlySearchButton | NoButton) &
  Omit<TextFieldProps, "startAdornment" | "endAdornment" | "afterField" | "onChange" | "variant">;

type SearchFieldBaseInternalProps = SearchFieldBaseProps & {
  wrapperStyle?: CSSProperties;
};

type SearchFieldAutocompleteSharedProps = {
  /**
   * Called when the user selects a suggestion.
   */
  onSelectSuggestion?: (option: AutocompleteOption) => void;
  /**
   * Open the suggestions listbox when the input receives focus.
   */
  openSuggestionsOnFocus?: boolean;
  /**
   * Maximum height of the suggestions listbox. A number is interpreted as px.
   */
  suggestionsMaxHeight?: string | number;
};

type SearchFieldWithoutAutocomplete = SearchFieldAutocompleteSharedProps & {
  suggestions?: never;
  suggestionsLabel?: never;
};

type SearchFieldWithAutocomplete = SearchFieldAutocompleteSharedProps & {
  /**
   * The list of suggestions to display in a listbox below the field.
   * No filtering is applied — pass the already-correct (pre-filtered) list.
   * If autocomplete should stay enabled while there are temporarily no matches,
   * pass an empty array instead of `undefined` or `null`. Changing this prop
   * from an absent value to an array switches between the regular and
   * autocomplete variants, which causes a re-render, drops input focus, and
   * hides the listbox.
   */
  suggestions: AutocompleteOption[];
  /**
   * Accessible label for the suggestions listbox.
   */
  suggestionsLabel: string;
};

export type SearchFieldAutocompleteEnhancements =
  | SearchFieldWithoutAutocomplete
  | SearchFieldWithAutocomplete;

const cx = c.bind(styles);
const rootClassName = "purpur-search-field";

export const SearchFieldBase = forwardRef<HTMLInputElement, SearchFieldBaseInternalProps>(
  (
    {
      ["data-testid"]: dataTestId,
      className,
      clearButtonAriaLabel,
      disabled,
      submitBehavior = "form",
      iconOnlySearchButton,
      onClear,
      onSearch,
      searchButtonLabel,
      variant,
      formRef,
      wrapperStyle,
      ...props
    },
    ref
  ) => {
    const getTestId = (name: string) => (dataTestId ? `${dataTestId}-${name}` : undefined);
    const hasValue =
      typeof props.value === "number" ? props.value !== undefined : props.value?.length;
    const hasClearButton = hasValue && !disabled && !props.readOnly && !props.loading;
    const isManualSubmit = submitBehavior === "manual";

    const classes = cx(className, rootClassName, {
      [`${rootClassName}--no-gap`]: variant === "button-attached",
      [`${rootClassName}--has-clear-button`]: hasClearButton,
    });

    const textFieldProps: TextFieldProps = {
      ref,
      className: classes,
      clearButtonAriaLabel,
      disabled,
      onClear,
      startAdornment: !iconOnlySearchButton ? (
        <IconSearch size="xs" className={cx(`${rootClassName}__search-icon`)} key="1" />
      ) : null,
      "data-testid": getTestId("text-field"),
      type: "search" as const,
      ...props,
    };

    const searchButton =
      variant !== "no-button" ? (
        <SearchFieldButton
          type={isManualSubmit ? "button" : "submit"}
          onClick={isManualSubmit ? (event) => onSearch?.(event) : undefined}
          disabled={disabled}
          iconOnly={iconOnlySearchButton}
          label={searchButtonLabel}
          data-testid={getTestId("button")}
        />
      ) : null;

    if (isManualSubmit) {
      const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        props.onKeyDown?.(event);

        if (event.key === "Enter" && !event.defaultPrevented) {
          event.preventDefault();
          onSearch?.(event);
        }
      };

      return (
        <div
          className={cx(`${rootClassName}__wrapper`)}
          data-testid={dataTestId}
          style={wrapperStyle}
        >
          <TextField {...textFieldProps} onKeyDown={handleKeyDown} afterField={searchButton} />
        </div>
      );
    }

    return (
      <form
        ref={formRef}
        className={cx(`${rootClassName}__wrapper`)}
        data-testid={dataTestId}
        style={wrapperStyle}
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          onSearch?.(e);
        }}
      >
        <TextField {...textFieldProps} afterField={searchButton} />
      </form>
    );
  }
);

SearchFieldBase.displayName = "SearchFieldBase";
